<template>

    <div>
        <div class="row">
            <div class="col-6">

                <AdminBlock title="Current Movement">
                    <MovementSelect
                        :movements="job.movements"
                        :job="job"
                        remove-margin
                        @update-movement="updateMovement"
                    />
                </AdminBlock>

                <AdminBlock title="Job Details">

                    <p class="mb-2">
                        <strong>Job:</strong>
                        {{ job.reference }}
                    </p>

                    <p class="mb-2">
                        <strong>
                            Journey start:
                        </strong>
                        <span v-if="job.collection_company.length > 0">
                            {{ job.collection_company + ', ' }}
                        </span>
                        {{ job.collection_location }}
                    </p>

                    <p v-if="job.vehicle" class="mb-2">
                        <strong>
                            Vehicle:
                        </strong>
                        <span v-if="job.vehicle?.reg">{{ job.vehicle.reg }}</span>
                        <span v-html="job.vehicle?.description || ''"></span>
                        <span v-if="job.vehicle?.vehicle_type?.type" class="text-nowrap">
                            ( {{ job.vehicle.vehicle_type.type }} )
                        </span>
                    </p>

                    <p v-if="job.customer" class="mb-2">
                        <strong>
                            Customer:
                        </strong>
                        {{ job.customer?.name || '' }}
                    </p>

                    <p v-if="job.contract" class="mb-2">
                        <strong>
                            Contract:
                        </strong>
                        {{ job.contract?.name || '' }}
                    </p>

                    <p class="mb-0">
                        <strong>
                            Purchase Order (Client Ref):
                        </strong>
                        {{ job.client_ref }}
                    </p>

                </AdminBlock>

                <AdminBlock title="Delivery">

                    <div class="form-group mb-4">
                        <GoogleMap
                            :location="internalDeliveryLocation"
                            @location-changed="updateLocation"
                        />
                    </div>

                    <div class="form-group">
                        <label for="delivery-company">
                            <strong>Delivery Company:</strong>
                        </label>
                        <input
                            type="text"
                            class="form-control"
                            v-model="internalDeliveryCompany"
                            placeholder="Enter delivery company"
                            id="delivery-company"
                        >
                    </div>

                </AdminBlock>

            </div>

            <div class="col-6">

                <AdminBlock title="Route">
                    <estimate-change-destination-route
                        :movements="adjustedMovements"
                        :mileage="internalMileage"
                        :selected-movement="selectedMovement"
                        @update-mileage="updateMileage"
                        @update-route-leg-distances="updateRouteLegDistances"
                    />
                </AdminBlock>

                <AdminBlock title="Price">
                    <RoutePriceChangeDestination
                        :contract_id="job.contract_id"
                        :vehicle_id="job.vehicle_id"
                        :price="job.price"
                        :mileage="internalMileage"
                        :fuel="internalPriceIncludesFuel"
                        :adblue="internalPriceIncludesAdblue"
                        @update-price="updatePrice"
                        @update-fuel="updateFuel"
                        @update-adblue="updateAdblue"
                    />
                </AdminBlock>

            </div>

        </div>

        <AdminBlock>
            <div class="d-flex justify-content-end">
                <a :href="generateCancelRoute()" class="btn btn-sm btn-danger me-2">
                    Cancel
                </a>

                <button
                    type="button"
                    class="btn btn-sm btn-success"
                    @click="submitForm()"
                >
                    Submit
                </button>
            </div>
        </AdminBlock>
    </div>

</template>

<script>

import AdminBlock from "@/components/movements/components/AdminBlock.vue";
import axios from "axios";
import Swal from "sweetalert2";
import GoogleMap from "@/components/movements/components/GoogleMap.vue";
import RoutePriceChangeDestination from "@/components/movements/components/RoutePriceChangeDestination.vue";
import MovementSelect from "@/components/movements/components/MovementSelect.vue";
import EstimateChangeDestinationRoute from "@/components/movements/components/EstimateChangeDestinationRoute.vue";

export default {

    components: {
        AdminBlock,
        EstimateChangeDestinationRoute,
        GoogleMap,
        MovementSelect,
        RoutePriceChangeDestination,
    },

    props: {
        job: {
            type: Object,
            default: null,
            required: true
        },
    },

    data () {
        return {
            internalDeliveryCompany : this.job.delivery_company ?? '',
            internalMileage: this.job.mileage ?? '',
            internalCollectionLat: this.job.collection_lat ?? '',
            internalCollectionLng: this.job.collection_lng ?? '',
            internalDeliveryLat: this.job.delivery_lat ?? '',
            internalDeliveryLng: this.job.delivery_lng ?? '',
            internalDeliveryLocation: this.job.delivery_location ?? '',
            internalDeliveryLocale: this.job.delivery_locale ?? '',
            internalPrice: this.job.price ?? 0,
            internalPriceIncludesFuel: this.job.price_includes_fuel ?? false,
            internalPriceIncludesAdblue: this.job.price_includes_adblue ?? false,
            selectedMovement: null,
            routeLegDistance: [],
        }
    },

    computed: {
        adjustedMovements() {
            if (!this.selectedMovement) {
                return this.job.movements;
            }

            return this.job.movements.map((m) => {
                if (m.id === this.selectedMovement.id) {
                    return {
                        ...m,
                        delivery_lat: parseFloat(this.internalDeliveryLat),
                        delivery_lng: parseFloat(this.internalDeliveryLng)
                    };
                }
                return m;
            });
        },
    },

    methods: {
        updateLocation(values) {
            this.internalDeliveryLat = values.latitude.toFixed(10);
            this.internalDeliveryLng = values.longitude.toFixed(10);
            this.internalDeliveryLocation = values.location;
            this.internalDeliveryLocale = values.locale;
        },

        updateMileage(mileage) {
            this.internalMileage = mileage;
        },

        updateRouteLegDistances(distances) {
            this.routeLegDistance = distances;
        },

        updatePrice(values) {
            this.internalPrice = values.price;
        },

        updateFuel(value) {
            this.internalPriceIncludesFuel = value;
        },

        updateAdblue(value) {
            this.internalPriceIncludesAdblue = value;
        },

        updateMovement(movement) {
            this.selectedMovement = movement;
        },

        generateCancelRoute() {
            return route('movements.jobs.show', {job: this.job.id});
        },

        generateFormRoute() {
            return route('api.movements.jobs.change-destination', this.job);
        },

        submitForm() {

            const data = {
                job_id: this.job.id,
                delivery_company: this.internalDeliveryCompany,
                delivery_location: this.internalDeliveryLocation,
                delivery_locale: this.internalDeliveryLocale,
                delivery_lat: Number(parseFloat(this.internalDeliveryLat).toFixed(10)),
                delivery_lng: Number(parseFloat(this.internalDeliveryLng).toFixed(10)),
                price: this.internalPrice,
                price_includes_fuel: this.internalPriceIncludesFuel,
                price_includes_adblue: this.internalPriceIncludesAdblue,
                mileage: this.internalMileage,
                selected_movement: this.selectedMovement?.id,
                route_leg_distance: this.routeLegDistance,
            };

            axios.post(this.generateFormRoute(), data)
                .then(response => {

                    Swal.fire({
                        toast: true,
                        position: 'top-end',
                        icon: 'success',
                        showConfirmButton: false,
                        title: 'Success!',
                        html: `<p class="mb-2">${response.data.message}</p>`,
                        width: '600px',
                        timer: 2000,
                        timerProgressBar: true,
                    }).then(() => {
                        window.location.href = this.generateCancelRoute();
                    });

                })
                .catch(error => {

                    let allErrors = '';

                    if (error.response?.data?.errors) {

                        const errorMessages = error.response.data.errors;

                        Object.entries(errorMessages).forEach(([, messages]) => {
                            messages.forEach(errorMessage => {
                                allErrors += '<p class="mb-0">' + errorMessage + '</p>';
                            });
                        });

                    } else {
                        allErrors = error.response?.data?.message;
                    }

                    const Toast = Swal.mixin({
                        toast: true,
                        position: 'top-end',
                        icon: 'error',
                        confirmButtonText: 'Dismiss',
                        confirmButtonColor: '#dc3545 ',
                        width: '600px',
                    });

                    Toast.fire({
                        title: "Error Changing Job Destination",
                        html: allErrors
                    });

                });

        }
    },

    mounted() {

    },

    watch: {
        selectedMovement(newMovement) {
            if (newMovement && newMovement.delivery_company) {
                this.internalDeliveryCompany = newMovement.delivery_company;
            } else {
                this.internalDeliveryCompany = this.job.delivery_company || '';
            }
        }
    },
}
</script>
